import React, { useEffect, useState } from 'react';
import { Repeat, Plus, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { adminService } from '../../../../services/adminService';
import { toast } from 'sonner';

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

// Compact per-client recurring surface (Phase 3). Purely a convenience surface
// over the global /admin/recurring-orders page — the create/edit/manage flows
// live there. This tab is the contextual entry point from the client-detail
// dialog. Users clicking "View" or "Manage all" navigate to that page.
export const RecurringTab = ({ clientDetail }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientDetail?.id) return;
    (async () => {
      setLoading(true);
      try {
        setRows(await adminService.getRecurringTemplates({ client_account_id: clientDetail.id }));
      } catch {
        toast.error('Failed to load recurring schedules for this client');
      } finally {
        setLoading(false);
      }
    })();
  }, [clientDetail?.id]);

  const goToGlobal = () => {
    navigate(`/admin/recurring-orders?client=${clientDetail.id}`);
  };

  return (
    <div className="space-y-4 mt-4" data-testid="client-recurring-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Recurring schedules for this client. Manage them from the global Recurring Orders page.
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={goToGlobal}
            data-testid="client-recurring-manage-all"
          >
            <ExternalLink className="h-4 w-4 mr-1" /> Manage all
          </Button>
          <Button size="sm" onClick={goToGlobal} data-testid="client-recurring-new">
            <Plus className="h-4 w-4 mr-1" /> New schedule
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500" data-testid="client-recurring-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border p-6 text-center text-sm text-gray-500"
          data-testid="client-recurring-empty">
          No recurring schedules yet. Create one from the global page.
        </div>
      ) : (
        <div className="rounded-md border divide-y">
          {rows.map((row) => (
            <div
              key={row.id}
              className="p-3 flex flex-wrap items-center justify-between gap-2"
              data-testid={`client-recurring-row-${row.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <Repeat className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium">{row.cadence_label}</span>
                  <Badge className={STATUS_STYLES[row.status] || 'bg-gray-100 text-gray-600'}
                    data-testid={`client-recurring-status-${row.id}`}>
                    {row.status}
                  </Badge>
                  <Badge variant="outline"
                    className={row.origin === 'admin' ? 'border-orange-400 text-orange-700 bg-orange-50' : 'border-blue-400 text-blue-700 bg-blue-50'}
                    data-testid={`client-recurring-origin-${row.id}`}>
                    {row.origin === 'admin' ? 'Admin-created' : 'Client-created'}
                  </Badge>
                </div>
                <div className="text-xs text-gray-600">
                  {row.item_count} item{row.item_count === 1 ? '' : 's'} · {row.location_name || 'Unknown location'}
                  {row.next_delivery_date && ` · next ${row.next_delivery_date}`}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={goToGlobal}
                data-testid={`client-recurring-view-${row.id}`}>
                View
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecurringTab;
